import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from '@hrms/ui';
import type { CreateGroupLearningRunBody, GroupLearningCompany, TrainingAudience } from '@hrms/shared';
import { tsr } from '../../lib/api.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { useCompanyGroup } from '../company-group/useCompanyGroup.js';
import { goToCompanyAt } from './openInCompany.js';
import type { AudiencePreviewState } from './assignModel.js';
import { isRunActive, nextPollInterval, runFromResponse } from './runModel.js';

/**
 * Hooks of All companies (docs/adr/0036-group-learning.md): who may see it, the cross-company
 * course library, one company's audience options, and a copy / assign run with its progress.
 * Everything goes through the typed `tsr.groupLearning` client; the API checks the caller's rights
 * company by company on every call, so nothing here is a security boundary.
 */

export const GROUP_LEARNING_KEYS = {
  all: ['group-learning'] as const,
  courses: ['group-learning', 'courses'] as const,
  audienceOptions: (orgId: string) => ['group-learning', 'audience-options', orgId] as const,
  run: (runId: string) => ['group-learning', 'run', runId] as const,
};

/**
 * Whether the signed-in person belongs to a company group they may see (the owner, or someone the
 * owner gave group-wide training), which is what shows the All companies page and its menu entry.
 * Hidden while loading; the API still enforces the permission and the rights in each company.
 */
export function useGroupLearningAccess() {
  const { group, isLoading } = useCompanyGroup();
  return { isMember: group !== null, isLoading, groupName: group?.name ?? null };
}

export function useGroupCourses(enabled = true) {
  const query = tsr.groupLearning.listCourses.useQuery({
    queryKey: GROUP_LEARNING_KEYS.courses,
    enabled,
    staleTime: 30_000,
  });
  return {
    data: query.data?.status === 200 ? query.data.body : null,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  };
}

export function refreshGroupCourses(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: GROUP_LEARNING_KEYS.courses });
}

/** One company's legal entities, departments and locations, for the per-company Who step. */
export function useAudienceOptions(orgId: string, enabled: boolean) {
  const query = tsr.groupLearning.audienceOptions.useQuery({
    queryKey: GROUP_LEARNING_KEYS.audienceOptions(orgId),
    queryData: { params: { orgId } },
    enabled,
    staleTime: 5 * 60_000,
  });
  return {
    options: query.data?.status === 200 ? query.data.body : null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * How many people an audience reaches in one company right now, for the Review step. The route is a
 * POST (the audience is a body), so it runs as a mutation each time the audience changes; a stale
 * answer for an earlier audience is dropped.
 */
export function useAudiencePreview(
  orgId: string,
  audience: TrainingAudience | null,
  enabled: boolean,
): AudiencePreviewState {
  const mutation = tsr.groupLearning.audiencePreview.useMutation();
  const [state, setState] = useState<AudiencePreviewState>({ status: 'idle' });
  const audienceKey = audience ? JSON.stringify(audience) : '';

  useEffect(() => {
    if (!enabled || !audience) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    mutation
      .mutateAsync({ params: { orgId }, body: { audience } })
      .then((response) => {
        if (cancelled) return;
        setState(response.status === 200 ? { status: 'ready', count: response.body.count } : { status: 'failed' });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
    // Only a different company or audience asks again; the mutation object changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, audienceKey, enabled]);

  return state;
}

/** Starts a run. Always send the same `key` for the same decision (see `stableRequestKey`). */
export function useStartGroupRun() {
  const mutation = tsr.groupLearning.createRun.useMutation();
  return {
    isPending: mutation.isPending,
    /** Resolves with the new run's id; rejects with the API error. */
    async start(body: CreateGroupLearningRunBody, key: string): Promise<string> {
      const response = await mutation.mutateAsync({ body, headers: { 'idempotency-key': key } });
      const run = runFromResponse(response);
      if (!run) throw new Error('The run did not start.');
      return run.id;
    },
  };
}

/** A run's progress, asked again every couple of seconds while any company is still moving. */
export function useGroupRun(runId: string | null) {
  const id = runId ?? '';
  const query = tsr.groupLearning.getRun.useQuery({
    queryKey: GROUP_LEARNING_KEYS.run(id),
    queryData: { params: { id } },
    enabled: runId !== null,
    refetchInterval: (q) => nextPollInterval(runFromResponse(q.state.data)),
  });
  const run = runFromResponse(query.data);
  return { run, isLoading: query.isLoading && runId !== null, isError: query.isError };
}

/** Retry one company of a run; the panel then polls again because that company is moving. */
export function useRetryGroupRunItem(runId: string) {
  const queryClient = useQueryClient();
  const mutation = tsr.groupLearning.retryRunItem.useMutation();
  return {
    isPending: mutation.isPending,
    async retry(orgId: string): Promise<void> {
      await mutation.mutateAsync({ params: { id: runId, orgId }, body: {} });
      await queryClient.invalidateQueries({ queryKey: GROUP_LEARNING_KEYS.run(runId) });
    },
  };
}

/**
 * Refreshes the course library once a run that was moving has settled, so the table shows the new
 * copies and counts without a reload. Mounted by the page, not the drawer, so closing the drawer
 * does not stop it.
 */
export function useRefreshLibraryWhenRunSettles(runId: string | null): void {
  const queryClient = useQueryClient();
  const { run } = useGroupRun(runId);
  const wasActive = useRef(false);
  useEffect(() => {
    wasActive.current = false;
  }, [runId]);
  useEffect(() => {
    const active = isRunActive(run);
    if (wasActive.current && !active) refreshGroupCourses(queryClient);
    wasActive.current = active;
  }, [run, queryClient]);
}

/**
 * Opens a page of another company at that company's own address, signed in as the caller there
 * (a one-time handoff code, because a session lives in one address's storage). The caller's own
 * company is just a link inside the app.
 */
export function useOpenInCompany() {
  const { actor } = useAuthenticatedSession();
  const navigate = useNavigate();
  const switchTo = tsr.auth.switchToLinkedOrganization.useMutation();
  const [openingOrgId, setOpeningOrgId] = useState<string | null>(null);

  async function open(company: GroupLearningCompany, path: string): Promise<void> {
    if (company.orgId === actor.orgId) {
      navigate(path);
      return;
    }
    setOpeningOrgId(company.orgId);
    try {
      const response = await switchTo.mutateAsync({ params: { orgId: company.orgId } });
      await goToCompanyAt(response.body.organization.slug, response.body.code, path);
    } catch {
      toast.error(`Couldn't open ${company.name} just now. Try again in a moment.`);
    } finally {
      setOpeningOrgId(null);
    }
  }

  return { open, openingOrgId, currentOrgId: actor.orgId };
}
